// Input — text input with optional leading icon. Focus ring and error
// border are driven by the wrapper via :focus-within and the --error modifier.

import { forwardRef, type InputHTMLAttributes } from 'react';
import Icon from './Icon';

export type InputSize = 'sm' | 'md';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Kebab-case Icon name shown at the leading edge. */
  iconStart?: string;
  /** Kebab-case Icon name shown at the trailing edge. */
  iconEnd?: string;
  /** Render the error (red) border. */
  error?: boolean;
  /** Control height: 'md' (default, 3.5rem) or compact 'sm' (2.5rem). */
  size?: InputSize;
}

const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { iconStart, iconEnd, error = false, size = 'md', className = '', ...rest },
  ref,
) {
  const classes = [
    'app-input',
    size === 'sm' && 'app-input--sm',
    iconStart && 'app-input--with-icon',
    iconEnd && 'app-input--with-icon-end',
    error && 'app-input--error',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const iconSize = size === 'sm' ? 16 : 18;

  return (
    <span className={classes}>
      {iconStart && (
        <Icon name={iconStart} size={iconSize} className='app-input__icon' aria-hidden='true' />
      )}
      <input ref={ref} className='app-input__control' aria-invalid={error || undefined} {...rest} />
      {iconEnd && (
        <Icon name={iconEnd} size={iconSize} className='app-input__icon-end' aria-hidden='true' />
      )}
    </span>
  );
});

export default Input;
